<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { CircleCheck } from 'lucide-vue-next'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDateTime, nowApiDateTime, toApiDateTime } from '@yuksalish/ui'
import { useShopfloorApi } from '../composables/useShopfloorApi'
import type { DowntimeEvent, DowntimeReason, EquipmentItem } from '../types/shopfloor'

const { t } = useI18n()
const { listDowntimeReasons, listDowntime, openDowntime, closeDowntime, listEquipment } = useShopfloorApi()

const events = ref<DowntimeEvent[]>([])
const reasons = ref<DowntimeReason[]>([])
const equipment = ref<EquipmentItem[]>([])
const loading = ref(true)
const error = ref('')
const reasonFilter = ref('')
const openOnly = ref(false)
const actionError = ref('')
const actionMessage = ref('')
const submitting = ref(false)

const showCreate = ref(false)
const form = reactive({
  reason_code: '',
  machine_id: null as number | null,
  start_time: new Date().toISOString().slice(0, 16),
  notes: '',
})

const filteredEvents = computed(() =>
  events.value.filter(
    (e) =>
      (!reasonFilter.value || e.reason_code === reasonFilter.value) &&
      (!openOnly.value || !e.end_time),
  ),
)
const totalMinutes = computed(() =>
  filteredEvents.value.reduce((sum, e) => sum + (e.duration_minutes ?? 0), 0),
)
const activeCount = computed(() => filteredEvents.value.filter((e) => !e.end_time).length)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listDowntime({ page: 1, per_page: 100 })
    events.value = result.items
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadReasons() {
  try {
    reasons.value = await listDowntimeReasons()
  } catch {
    reasons.value = []
  }
}

async function loadEquipment() {
  try {
    equipment.value = await listEquipment()
  } catch {
    equipment.value = []
  }
}

/** Localized downtime reason label, falling back to the backend name. */
function reasonLabel(code: string, fallback = ''): string {
  const key = `shopfloor.downtime.reasons.${code}`
  const value = t(key)
  return value === key ? fallback || code : value
}

onMounted(() => {
  void load()
  void loadReasons()
  void loadEquipment()
})

function openReport() {
  form.reason_code = reasons.value[0]?.code ?? ''
  form.machine_id = null
  form.start_time = new Date().toISOString().slice(0, 16)
  form.notes = ''
  actionError.value = ''
  actionMessage.value = ''
  showCreate.value = true
}

async function submitOpen() {
  actionError.value = ''
  if (!form.reason_code) {
    actionError.value = t('shopfloor.downtime.selectReason')
    return
  }
  submitting.value = true
  try {
    const created = await openDowntime({
      reason_code: form.reason_code,
      machine_id: form.machine_id,
      start_time: toApiDateTime(form.start_time),
      notes: form.notes.trim() || undefined,
    })
    events.value = [created, ...events.value]
    showCreate.value = false
    actionMessage.value = t('shopfloor.downtime.reported', { id: created.id })
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

async function close(event: DowntimeEvent) {
  actionError.value = ''
  if (!window.confirm(t('shopfloor.downtime.closeConfirm'))) return
  submitting.value = true
  try {
    const closed = await closeDowntime(event.id, {
      end_time: nowApiDateTime(),
    })
    events.value = events.value.map((e) => (e.id === closed.id ? closed : e))
    actionMessage.value = t('shopfloor.downtime.closed', { id: closed.id })
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('shopfloor.downtime.eyebrow') }}</p>
      <h2>{{ t('shopfloor.downtime.title') }}</h2>
      <p>{{ t('shopfloor.downtime.description') }}</p>
    </div>
    <button class="btn-primary" @click="openReport">{{ t('shopfloor.downtime.openDowntime') }}</button>
  </div>

  <!-- Summary cards -->
  <div class="summary-row">
    <div class="summary-card">
      <span class="summary-value">{{ totalMinutes }} {{ t('shopfloor.downtime.minutes', { count: totalMinutes }) }}</span>
      <span class="summary-label">{{ t('shopfloor.downtime.totalDowntime') }}</span>
    </div>
    <div class="summary-card" :class="{ active: activeCount > 0 }">
      <span class="summary-value">{{ activeCount }}</span>
      <span class="summary-label">{{ t('shopfloor.downtime.activeDowntime') }}</span>
    </div>
  </div>

  <!-- Filters -->
  <div class="filter-bar">
    <select v-model="reasonFilter" class="filter-select">
      <option value="">{{ t('shopfloor.downtime.selectReason') }}</option>
      <option v-for="r in reasons" :key="r.code" :value="r.code">{{ reasonLabel(r.code, r.name) }}</option>
    </select>
    <label class="check">
      <input v-model="openOnly" type="checkbox" />
      {{ t('shopfloor.downtime.openOnly') }}
    </label>
  </div>

  <!-- Loading -->
  <div v-if="loading" class="state-message"><p>{{ t('shopfloor.downtime.loading') }}</p></div>

  <!-- Error -->
  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('shopfloor.state.retry') }}</button>
  </div>

  <!-- Empty -->
  <div v-else-if="filteredEvents.length === 0" class="state-message">
    <p>{{ t('shopfloor.downtime.empty') }}</p>
  </div>

  <!-- Table -->
  <div v-else class="table-scroll">
  <table class="data-table">
    <thead>
      <tr>
        <th>{{ t('shopfloor.downtime.reason') }}</th>
        <th>{{ t('shopfloor.downtime.machine') }}</th>
        <th>{{ t('shopfloor.downtime.start') }}</th>
        <th>{{ t('shopfloor.downtime.end') }}</th>
        <th>{{ t('shopfloor.downtime.duration') }}</th>
        <th>{{ t('shopfloor.downtime.reportedBy') }}</th>
        <th>{{ t('shopfloor.assignments.operation') }}</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="e in filteredEvents" :key="e.id" :class="{ 'row-active': !e.end_time }">
        <td class="cell-reason">{{ reasonLabel(e.reason_code, e.reason_name) }}</td>
        <td>{{ e.machine_name ?? '—' }}</td>
        <td>{{ formatDisplayDateTime(e.start_time) || '—' }}</td>
        <td>{{ e.end_time ? (formatDisplayDateTime(e.end_time) || '—') : '—' }}</td>
        <td class="cell-duration">{{ e.duration_minutes != null ? e.duration_minutes + ' ' + t('shopfloor.downtime.minutes', { count: e.duration_minutes }) : t('shopfloor.downtime.ongoing') }}</td>
        <td>{{ e.reported_by }}</td>
        <td>
          <button v-if="!e.end_time" class="link-action icon-action danger" :title="t('shopfloor.downtime.close')" :aria-label="t('shopfloor.downtime.close')" @click="close(e)">
            <CircleCheck :size="18" aria-hidden="true" />
          </button>
        </td>
      </tr>
    </tbody>
  </table>
  </div>

  <!-- Report modal -->
  <div v-if="showCreate" class="modal-overlay" @click.self="showCreate = false">
    <form class="modal" @submit.prevent="submitOpen">
      <h3>{{ t('shopfloor.downtime.openDowntime') }}</h3>
      <label>{{ t('shopfloor.downtime.reason') }}
        <select v-model="form.reason_code" required>
          <option v-for="r in reasons" :key="r.code" :value="r.code">{{ reasonLabel(r.code, r.name) }}</option>
        </select>
      </label>
      <label>{{ t('shopfloor.downtime.machine') }}
        <select v-model="form.machine_id">
          <option :value="null">{{ t('shopfloor.downtime.noMachine') }}</option>
          <option v-for="e in equipment" :key="e.id" :value="e.id">{{ e.name }}</option>
        </select>
      </label>
      <label>{{ t('shopfloor.downtime.startTime') }}
        <input v-model="form.start_time" type="datetime-local" required />
      </label>
      <label>{{ t('shopfloor.orderDetail.notes') }}
        <input v-model="form.notes" type="text" />
      </label>
      <small v-if="actionError" class="error">{{ actionError }}</small>
      <div class="modal-actions">
        <button type="button" class="btn-secondary" @click="showCreate = false">{{ t('shopfloor.assignments.cancel') }}</button>
        <button type="submit" class="btn-primary" :disabled="submitting">
          {{ submitting ? t('shopfloor.downtime.reporting') : t('shopfloor.downtime.report') }}
        </button>
      </div>
    </form>
  </div>

  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>
</template>

<style scoped>
.page-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}
.eyebrow {
  margin: 0 0 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }

.summary-row {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
}
.summary-card {
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  padding: 20px 28px;
  text-align: center;
}
.summary-card.active { border-color: #f0c040; background: #fffdf5; }
.summary-value {
  display: block;
  font-size: 28px;
  font-weight: 800;
  color: #1a2b26;
}
.summary-label {
  display: block;
  font-size: 12px;
  color: #65736e;
  text-transform: uppercase;
  margin-top: 4px;
  font-weight: 600;
}

.state-message {
  padding: 40px 0;
  text-align: center;
  color: #5f6b6a;
}
.state-message.error { color: #b33a3a; }

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  overflow: hidden;
}
.data-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #65736e;
  background: #f6f8f7;
  border-bottom: 1px solid #e2e6e3;
}
.data-table td {
  padding: 12px 16px;
  font-size: 13px;
  color: #1a2b26;
  border-bottom: 1px solid #f0f2f1;
}
.data-table tr:last-child td { border-bottom: none; }
.row-active { background: #fffdf5; }
.cell-reason { font-weight: 600; }
.cell-duration { font-weight: 700; font-family: monospace; }

.filter-bar {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 20px;
}
.filter-select {
  padding: 8px 14px;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
  min-width: 220px;
}
.check { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #1a2b26; }

.link-action {
  color: #27705d;
  font-weight: 600;
  font-size: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}
.link-action:hover { text-decoration: underline; }
.link-action.danger { color: #b33a3a; }

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 20, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.modal {
  width: 380px;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.modal h3 { margin: 0; font-size: 16px; font-weight: 700; }
.modal label {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 600;
  color: #65736e;
  gap: 4px;
}
.modal input, .modal select {
  padding: 8px 12px;
  border: 1px solid #d4d8d5;
  border-radius: 6px;
  font-size: 13px;
  background: #fff;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.error { color: #b33a3a; font-size: 12px; }
.action-message {
  padding: 12px 16px;
  background: #dcefe7;
  color: #1f5c4b;
  border-radius: 8px;
  font-size: 13px;
  margin-top: 16px;
}

.btn-primary {
  display: inline-block;
  padding: 8px 20px;
  background: #27705d;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.btn-primary:disabled { opacity: 0.5; cursor: default; }
.btn-primary:hover:not(:disabled) { background: #1f5c4b; }
.btn-secondary {
  padding: 8px 16px;
  background: #fff;
  color: #27705d;
  border: 1px solid #27705d;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.btn-secondary:hover { background: #f0f5f3; }
</style>
